/*
 * Copyright (c) 2023. American Express Company
 */

// third-party
import React, { useEffect } from 'react';
import { IconInfo } from '@americanexpress/dls-react';

// models and services
import LiteralsService from 'src/services/literals/LiteralsService';
import { MeetYourPlanViewModel } from 'src/types/view-models/onboarding/meetYourPlan.view.model';
import PersonService from 'src/services/person.service';
import { DLSSize } from 'src/types/dls-size.enum';

// components
import Drawer from 'src/components/drawer/Drawer';
import OnboardingButtons from 'src/components/onboarding-buttons/OnboardingButtons';
import OnboardingHeader from 'src/components/onboarding-header/OnboardingHeader';
import Callout from '../../../../atoms/callout/Callout';
import Typography from 'src/components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';

// icons and styles
import styles from './MeetYourPlan.module.scss';

export default function MeetYourPlan({
    yourPerson,
    back,
    meetYourPlanNext,
    drawerProps
}: MeetYourPlanViewModel): JSX.Element {
    // Prompt an initial render of the income graph
    useEffect(() => {
        window.dispatchEvent(new Event('resize'));
    }, []);

    const icon: JSX.Element = <IconInfo size={DLSSize.MEDIUM} />;
    const customContent: JSX.Element = (
        <ul className={styles.customContent}>
            <li>
                <Typography variant={TypographyVariants.SUBTEXT_BOLD}>
                    {LiteralsService.getLiteral('Onboarding.meetYourPlan.list1Part1')}
                </Typography>
                <Typography variant={TypographyVariants.SUBTEXT}>
                    {LiteralsService.getLiteral('Onboarding.meetYourPlan.list1Part2')}
                </Typography>
            </li>
            <li>
                <Typography variant={TypographyVariants.SUBTEXT_BOLD}>
                    {LiteralsService.getLiteral('Onboarding.meetYourPlan.list2Part1')}
                </Typography>
                <Typography variant={TypographyVariants.SUBTEXT}>
                    {LiteralsService.getLiteral('Onboarding.meetYourPlan.list2Part2')}
                </Typography>
            </li>
        </ul>
    );

    return (
        <Drawer drawerProps={drawerProps} withoutStickyHeader>
            <div className={styles.introContent}>
                <OnboardingHeader
                    title={LiteralsService.getLiteral('Onboarding.meetYourPlan.title', [
                        yourPerson ? `, ${PersonService.firstName(yourPerson)}` : ''
                    ])}
                    description={LiteralsService.getLiteral('Onboarding.meetYourPlan.description')}
                    onboardingStep={0}
                />
                <Typography component="p" variant={TypographyVariants.SUBTEXT}>
                    {LiteralsService.getLiteral('Onboarding.meetYourPlan.projections')}
                </Typography>
            </div>
            <Callout
                icon={icon}
                heading={LiteralsService.getLiteral('Onboarding.meetYourPlan.optionsBoxTitle')}
                withButton={false}
            >
                {customContent}
            </Callout>
            <div className={styles.buttonsContainer}>
                <Typography component="p" variant={TypographyVariants.HEADING_XS} className={styles.buttonsHeader}>
                    {LiteralsService.getLiteral('Onboarding.meetYourPlan.whenReady')}
                </Typography>
                <OnboardingButtons
                    back={back}
                    backButtonEventName={'o4 Meet plan Back'}
                    next={meetYourPlanNext}
                    nextButtonTitle={LiteralsService.getLiteral('Onboarding.meetYourPlan.next')}
                />
            </div>
        </Drawer>
    );
}
